import {_, classNames, React, useAsObservableSource, useCallback, utils, computed, action, observable, observer, computedFn, Tooltip2} from '@api'
import {Tooltip} from '@material-ui/core'
import {BuilderCardStore, CardImageData, ICatalogItemDetailsPathElement, useBuilderCardStore} from '@modules'
import {imageBreakpoints} from '@stores'
import {autorun, reaction, runInAction, toJS} from 'mobx'
import {useEffect} from 'react'
import * as css from './BuilderCard.css'
import {ImageRecord, ui} from '@stores'
import {MyImage, MyImageStore} from 'centric-ui-components'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {StoresInstance} from '@stores'

/// region Store

export class BuilderCardImageStore {
  constructor(public image: ImageRecord, public index: number, public card: BuilderCardStore) {
    if (_.isString(image)) {
      image = {exId: image}
    }

    if (!_.isObject(image)) {
      console.error(`Image must be an ImageRecord`, {image})
      throw new Error(`Image must be an ImageRecord`)
    }

    this.originalSize = _.pickBy(_.pick(image, ['width', 'height'])) // remove {width: null, height: null}
  }

  @action select = () => {
    this.card.selectedImageIndex = this.index
  }

  get catalog() { return this.card.catalog }

  @computed get path(): ICatalogItemDetailsPathElement[] {
    const {catalog, card, image, card: {workspace}} = this
    return catalog.pathForData(image, card, workspace)
  }

  @observable originalSize: { width?: number, height?: number }
  @observable overrideSize

  @action onMeasureImage = ({width, height, image}) => {
    // console.log(`onMeasureImage`, {width, height})
    const {size} = this
    if (size.width != width || size.height != height) {
      const {predictedSize, card: {prototypicalCardSize, catalog: {cardSizeSliderAsMultiplier}}, image} = this
      // console.debug(`${image.exId} - Card image size mismatch - predicted : ${predictedSize.width}, ${predictedSize.height} - actual: ${width}, ${height} - proto: ${prototypicalCardSize.width}, ${prototypicalCardSize.height} - native: ${image.width}, ${image.height}`, {cardSizeSliderAsMultiplier})
      this.overrideSize = {width: width ?? size.width, height: height ?? size.height} // need to do math to convert it
    }
  }

  @computed get isActive() {
    const {catalog: {idFor}, cardImageData: {__parent}, card: {id}} = this
    if (idFor(__parent) === id) return this.card.isActive

    const {workspace: {dataMap: {active}}} = this
    if (active) return _.get(__parent, active)
  }

  @computed get predictedSize() {
    const {originalSize, card: {catalog: {cardSizeSliderAsMultiplier}, prototypicalCardSize}} = this

    if (!_.isEmpty(originalSize)) {
      let r = _.cloneDeep(originalSize), ratio = originalSize.width / originalSize.height

      //if (r.width > prototypicalCardSize.width || r.height > prototypicalCardSize.height) {
      // Scale down to the largest dimension
      if (r.width > r.height) {
        r.width = prototypicalCardSize.width - 1
        r.height = Math.round(r.width * ratio)
      } else {
        r.height = prototypicalCardSize.height - 1
        r.width = Math.round(r.height * ratio)
      }
      //}
      // else {
      //   // Scale up?
      //   if (r.width > r.height) {
      //     r.width = prototypicalCardSize.width
      //     r.height = Math.round(ratio * r.width)
      //   }
      //   else {
      //     r.height = prototypicalCardSize.height
      //     r.width = Math.round(r.height * ratio)
      //   }
      //}

      return r
    }

    return prototypicalCardSize
  }

  @computed get hasSize() {
    return !_.isEmpty(this.overrideSize ?? this.originalSize)
  }

  @computed get sizeArr() {
    const {size} = this
    return [size.width, size.height]
  }

  @observable.ref imageStore: MyImageStore

  @computed get workspace() {
    const {path, card, catalog: {workspaces}} = this,
      item = _.last(path)

    if (!item) return card.workspace

    return workspaces.byIndexScalar[item.esIndex] ?? card.workspace
  }

  @computed get autoTransparency() {
    const {workspace} = this,
      autoTransparency = workspace?.catalog?.features?.autoTransparency ?? false

    return autoTransparency
  }

  @computed get imageSize() {
    const {card: {levelOfDetail}, catalog, catalog: {settings: {imageRatio}}} = this,
      size = Math.max(1, catalog.cardSizeSliderAsMultiplier) * (levelOfDetail ? 400 : 400),
      imageSize = {width: imageRatio === 'landscape' ? size * 1.33 : size, height: imageRatio === 'portrait' ? size * 1.33 : size}

    return imageSize
  }

  @computed get imageSizeBreakpoint() {
    const {imageSize} = this,
      imageNumber = Math.max(imageSize.width, imageSize.height)
    return imageBreakpoints.find(v => v >= imageNumber) ?? _.last(imageBreakpoints)
  }

  @computed get imageUrl() {
    const {image, imageSize, autoTransparency, card: {lowDefImageSize, highDefImageSize}, catalog: {imageUrlFor}} = this

    //console.log({images, _imageIndex})
    // imageUrlFor will do this automatically for us now
    // imageSize = normalizeImageSize(shapeStore?.imageSize ?? scaledSize, this.size)

    // If we do largeImage here it breaks native D&D image preview in chrome
    const lowDef = imageUrlFor(image, {imageSize, c8Size: lowDefImageSize, imageType: 'png', autoTransparency})
    const highDef = !image ? lowDef : imageUrlFor(image, {imageSize, c8Size: highDefImageSize, imageType: 'png', autoTransparency})

    return {defaultImageUrl: lowDef, highDefUrl: highDef}
  }

  @computed get cardImageData(): CardImageData {
    const {image, card: {catalog: {idFor}, cardTitle}, imageUrl} = this
    return {
      imageUrl: imageUrl,
      id: idFor(image.__parent ?? image, false),
      imageId: idFor(image, false),
      filename: cardTitle,
      image: this,
      __parent: image.__parent,
      __path: image.__path
    }
  }

  @computed get c8ImageUrl() {
    const {image, card: {catalog: {imageUrlFor}}} = this
    return imageUrlFor(image, {c8Size: 'smallImage', source: 'c8'})
  }

  /** After render, depends on screen zoom, flex, catalog zoom, etc **/

  @computed get size(): { width?: number, height?: number } {
    const {overrideSize} = this
    if (!_.isEmpty(overrideSize)) return overrideSize

    return this.predictedSize
  }

  @computed get right() {
    return Number(this.offsetLeft + Number(this.size?.width))
  }

  @computed get offsetLeft() {
    const {card: {catalog, imagesToRender}, index, size} = this

    // if (!size?.width) return 0

    if (imagesToRender.length <= 1 || index === 0 || !size) {
      return 0
    } else {
      const right = imagesToRender[index - 1].right,
        width = Number(size?.width) * (1 - (catalog.imageSource.stackedCards?.spread ?? 0))
      return _.isNaN(right) ? width : right - width
    }
  }

  @computed get width() { return Number(this.right - this.offsetLeft)}
}

export class MyBuilderCardImageStore extends VipLocalStore {
  @computed get fieldName() {
    const {imageStore: {image: {__parent, __path}}, stores: {workspaces: {availableWorkspaces, wrapperById}, catalog: {imageSource: {ids}}}} = this
    const fieldName = __parent?.__path ?? wrapperById[_.last(availableWorkspaces.filter(({_id}) => _.isEmpty(ids) || ids.includes(_id)))?._id]?.visible ? __path : undefined
    return fieldName
  }

  // @action onMeasureImage = ({width, height}) => {
  //   // console.log({width, height, id})
  //   this.imageStore.size = {width, height}
  // }

  @action onMouseEnterImage = (e: React.MouseEvent) => {
    const {store, imageStore: image, imageStore: {image: {__parent}}} = this
    // console.log(`onMouseEnterImage`, image)
    if (__parent) {
      store.hoverSubItem = image
    }
    store.onMouseEnter(e)
  }

  @computed get imageStore() {
    return this.props.image
  }

  @computed get isSelectedImage() {
    const {props: {index}, store: {selectedImageIndex}} = this
    return index === selectedImageIndex
  }

  @computed get isPreviousSelectedImage() {
    const {isSelectedImage, props: {index}, store: {selectedImageIndex, imagesToRender}} = this

    return !isSelectedImage && (index === selectedImageIndex - 1 || (selectedImageIndex === 0 && index + 1 === imagesToRender.length))
  }

  @computed get isNextSelectedImage() {
    const {isSelectedImage, props: {index}, store: {selectedImageIndex, imagesToRender}} = this

    return !isSelectedImage && (index === selectedImageIndex + 1 || (index === 0 && selectedImageIndex + 1 === imagesToRender.length))
  }

  @action onMouseOutImage = (e: React.MouseEvent) => {
    // console.log(`onMouseOutImage`, image)
    const {store, imageStore} = this
    if (store.hoverSubItem == imageStore) {
      store.hoverSubItem = null
    }
  }

  get store() { return this.props.cardStore}

  @action onClickImage = (isDoubleClick: boolean) => (e: React.MouseEvent) => {
    const {catalog, itemName: name, fieldName, associatedWorkspace, store, imageStore, imageStore: {image: {__parent, __path}, cardImageData: {imageId, id}}} = this,
      field = catalog._fieldByName[fieldName]

    //  DEBUG && console.debug(`${isDoubleClick ? 'Double-Click' : 'Click'} image of ${catalog.catalogItemName} ${store.id} - image ${id} ${field?.esIndex ? `(${field.esIndex})` : ''}`)

    if (/*imageSource.view != 'first' && */ associatedWorkspace && associatedWorkspace._id !== catalog.id) {
      const library = catalog.libraryByIndex[associatedWorkspace.catalog.index]
      if (library) {
        const parentId = catalog.idFor(!__parent ? null : __parent?.__path ? __parent : __parent[__path], false)

        if (!parentId) {
          console.debug(`Unable to find id field on`, __parent, associatedWorkspace, __path)
        } else {
          isDoubleClick && DEBUG && console.debug(`Opening child object ${associatedWorkspace.catalog.index}/_doc/${parentId} - image ${imageId}`)

          if (field.card?.noOpenNestedImage) {
            store.onClick(e, {library, isDoubleClick, id: parentId, imageId, name, imageStore})
          } else {
            store.onClick(e, {library, isDoubleClick, id: parentId, imageId, name, imageWorkspace: associatedWorkspace._id, imageStore})
          }

          // Prevent our parent card from doing its own double click logic
          // e.stopPropagation()
        }
      } else {
        store.onClick(e, {id, isDoubleClick, imageId: imageId, name, imageStore})
      }
    } else {
      store.onClick(e, {id, isDoubleClick, imageId, name, imageStore})
    }

    // isDoubleClick && store.onDoubleClick(e)  // This will always cause opening the MPV
    /** This will stop logic in {@see BuilderCardStore.onDoubleClick} but allows us to select the right double clicked image, however logic could be different here... */
    e.stopPropagation()
  }

  @action onCreateImageStore = (imgStore: MyImageStore) => {
    // console.log({imgStore, image, id: image.image.exId})
    this.imageStore.imageStore = imgStore
  }

  @computed get objectFit() { return this.associatedWorkspace?.catalog?.features?.card?.objectFit ?? 'contain' }

  @computed get imagePadding() { return this.associatedWorkspace?.catalog?.features?.card?.imagePadding ?? undefined }

  @computed get alignSelf() { return this.associatedWorkspace?.catalog?.features?.card?.alignSelf ?? undefined }

  @computed get associatedWorkspace() {
    const {props: {cardStore: {ws}}, catalog, imageStore: {image: {__path}}} = this
    return __path ? catalog.workspaceForField(catalog._fieldByName[__path.replace('.images', '')]) : ws
  }

  @computed get itemName() {
    const {catalog, store: {imageSourceView}, imageStore: {image: {__parent}}, associatedWorkspace, store} = this
    return (__parent && associatedWorkspace && associatedWorkspace != catalog.workspace
            ? _.get(__parent, associatedWorkspace.dataMap?.title)
            : _.get(store.data, catalog.dataMap.title)) ?? catalog.catalogItemName ?? ''
  }

  @computed get tooltipTitle() {
    const {catalog, itemName, store: {props: {isPopover}, imageSourceView}, imageStore: {image: {__parent}}, associatedWorkspace, store} = this
    return isPopover ? ''
                     : imageSourceView === 'stack' || imageSourceView === 'grid' || store.levelOfDetail === 0
                       ? __parent && associatedWorkspace
                         ? `${itemName}\n(${associatedWorkspace.catalog.catalogItemName})`
                         : itemName : itemName
  }

  registerEffects = () => {
    const {catalog, store} = this
    const toDispose = [
      reaction(() => store.selectNextLoadedImage, () => {
        const {imageStore: {imageStore}} = this
        if (!imageStore.loaded) {
          toDispose.push(
            reaction(() => imageStore.loaded, () => {
              runInAction(() => {
                store.selectNextLoadedImage = false
                store.selectedImageIndex = this.props.index
              })
            }))
        }
      })
    ]

    return () => {
      toDispose.forEach(d => d())
    }
  }

  constructor(stores: StoresInstance, public props: MyProps & { cardStore: BuilderCardStore }) {
    super(stores)

    //{cardImageData: {qrCodeUrl, filename, imageId, id}, image: {__parent, __path}, size} = image,

    // If we're already open to a details view make single click show our item
    // onClickImage = useCallback((e: React.MouseEvent) => {
    //   if (!sidebar.right.showDetailsView || imageSource.view == 'first') return
    //
    //   if (field?.esIndex) {
    //     const library = catalog.libraryByIndex[field.esIndex]
    //     if (library) {
    //       const parentId = catalog.idFor(__parent?.__path ? __parent : __parent[__path], false)
    //
    //       if (!parentId) {
    //         console.debug(`Unable to find id field on`, __parent)
    //       } else {
    //         DEBUG && console.debug(`Opening child object ${field.esIndex}/_doc/${parentId} - image ${imageId}`)
    //         sidebar.right.navigateToLibrary({id: library.id, where: [{value: parentId}], imageId}, true)
    //       }
    //     }
    //   } else {
    //     sidebar.right.navigateToLibrary({where: [{value: id}], imageId}, true)
    //   }
    // }, [id, imageId, __parent, store]),

  }
}

export const MyBuilderCardImageStoreContext = React.createContext<MyBuilderCardImageStore>(null)
export const useMyBuilderCardImageStore = () => React.useContext(MyBuilderCardImageStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  image: BuilderCardImageStore,
  index: number
}

/// endregion

export const BuilderCardImage = observer<MyProps, any>(function BuilderCardImage({image, index}, ref) {
  const cardStore = useBuilderCardStore(),
    obsProps = useAsObservableSource({cardStore, image, index}),
    store = useVipLocalStore((stores) => new MyBuilderCardImageStore(stores, obsProps)),
    {
      store: {isInWhiteboard, imagePaths, qrCodeUrl, imageSourceView, everSelected, convertToDataURL, stackedImageStyleForItem, onContextMenu, selectedNestedItem, showQRCode},
      catalog: {settings: {imageRatio}}, onMouseOutImage, onCreateImageStore, onClickImage, onMouseEnterImage, tooltipTitle,
      imageStore: {autoTransparency, imageSizeBreakpoint, isActive, cardImageData: {id, filename, imageId}, image: {__parent, __path}},
      isSelectedImage, isNextSelectedImage, isPreviousSelectedImage, objectFit, imagePadding, alignSelf, associatedWorkspace
    } = store,
    pathIndex = !__path ? 0 : (imagePaths.indexOf(__path) + 1)

  useEffect(() => store.registerEffects(), [])
  //console.log(toJS({image, __parent, __path, tooltipTitle, associatedWorkspace}, {recurseEverything: true}))

  // if (!store.store.showHoverButtons && !isSelectedImage) return null

  return (
    <MyBuilderCardImageStoreContext.Provider value={store}>
      <div className={classNames(css.image, css[imageRatio],
        {
          [css.selectedImage]: isSelectedImage,
          [css.previousSelectedImage]: isPreviousSelectedImage,
          [css.nextSelectedImage]: isNextSelectedImage
        })}
           data-selected={selectedNestedItem && selectedNestedItem === __parent}
           data-active={isActive}
           data-index={index}
           data-path-index={pathIndex}
           data-path={__path || ''}
           data-image-id={imageId}
           data-object-fit={objectFit}
           data-id={id}
           style={{
             ...(associatedWorkspace?.catalog?.features?.card?.cssStyles ?? {}),
             '--index': index, '--path-index': pathIndex, '--z-index': index,
             '--image-source-object-fit': objectFit,
             '--card-image-padding': imagePadding,
             '--card-image-align-self': alignSelf,
             ...stackedImageStyleForItem(index),
           } as any}
      >
        <Tooltip2 // way faster than Mui
          placement='bottom'
          content={tooltipTitle}
          //usePortal={false}
          hoverOpenDelay={100}
          hoverCloseDelay={0}
        >
          <MyImage
            ref={ref}
            imageId={imageId}
            rawImageSize={imageSizeBreakpoint}
            transparent={autoTransparency}
            // title={tooltipTitle}
            onContextMenu={__parent != null ? onContextMenu({data: __parent, meta: 'image', imageUrl: image?.cardImageData?.imageUrl?.highDefUrl}) : undefined}
            onCreateStore={onCreateImageStore}
            onDoubleClick={onClickImage(true)}
            onClick={onClickImage(false)}
            onMeasureImage={!image.hasSize && (isInWhiteboard || imageSourceView === 'stack') ? image.onMeasureImage : undefined}
            style={{
              '--index': index, '--path-index': pathIndex, '--z-index': index,
              '--image-source-object-fit': objectFit,
              ...stackedImageStyleForItem(index)
            } as any}
            showCode={showQRCode}
            imageUrl={image.cardImageData.imageUrl}
            c8ImageUrl={image.c8ImageUrl}
            linkUrl={qrCodeUrl}
            lazy={!isNextSelectedImage && !isPreviousSelectedImage && !isSelectedImage}
            filename={filename}
            onMouseEnter={onMouseEnterImage}
            onMouseOut={onMouseOutImage}
            convertToDataURL={ui.settings.dragImagesToDesktop === true && (everSelected /*|| everMousedOver*/) && convertToDataURL}
            // onGetBackgroundColor={store.onGetImageBackgroundColor}
            //width={100}
            //height={100}
            draggable={utils.isSafari} // If we try to mark the image as draggable in LOD1 the image preview won't actually work in chrome due to image sizes, instead we want to just use the <img /> drag start code instead.  We want draggable for D&D into powerpoint and D&D on safari to disk
            blurOnLoad
            //loader={<LoadingImagePlaceholder/>}
          />
        </Tooltip2>
      </div>
    </MyBuilderCardImageStoreContext.Provider>)
}, {forwardRef: true})



// WEBPACK FOOTER //
// ./src/modules/catalog/product/productCard/components/BuilderCard/BuilderCardImage.tsx